/**
 * The constants and the action schema `UiSpecV1` (shared/schemas/uiSpec.ts) and its controls (shared/schemas/uiControls/) both
 * stand on. Split out of `uiSpec.ts` so the controls can import them without a cycle; `uiSpec.ts` re-exports all of it.
 *
 * OURS: the workbench defines this file; AIOE mirrors it with the header line, as it does `uiSpec.ts`.
 */
import { z } from "zod";
/** The seven tones of the design language (`webview-ui/src/components/ui/Art.tsx` VIEW_TONES). */
export const UI_TONES = ["teal", "violet", "amber", "blue", "rose", "green", "slate"];
/** Every bound. Raising one is a decision: the tests name them. */
export const UI_LIMITS = {
    depth: 8,
    nodes: 200,
    /** Characters of one text node, one markdown-free paragraph. */
    text: 4_000,
    /** Characters of a label, a title, an option. */
    label: 120,
    /** Characters of an option's description or a stat's hint. */
    hint: 240,
    /** Serialised bytes of one spec. */
    bytes: 64 * 1024,
    /** Blocks taken from one reply. */
    blocksPerReply: 5,
    tableRows: 200,
    tableColumns: 12,
    options: 12,
    choiceCards: 6,
    buttons: 8,
    rowActions: 4,
    children: 40,
    fieldName: 32,
    /** Stages of one `steps` tracker, or dots of its history strip. */
    steps: 24,
    /** Options of a select-like control (a country list is long; a bound list is capped at `boundRows`). */
    controlOptions: 60,
    /** Columns of a data grid. */
    gridColumns: 24,
    /** Rows a bound list gives a control, after which it is cut (a binding's own answer is already capped at 256 kB). */
    boundRows: 2_000,
    /** Points of a literal chart, and series of one. */
    chartRows: 200,
    chartSeries: 8,
    /** Panels of tabs, an accordion or a carousel. */
    panels: 12,
    /** Places, route points and arcs of a map. */
    mapPlaces: 500,
    /** Files a drop zone takes, and bytes of each (the action receives them as attachments). */
    dropFiles: 4,
    dropFileBytes: 512 * 1024,
};
/** A field's name, and the key its value has in the event. */
export const UI_FIELD_NAME = /^[a-z][a-z0-9_]{0,31}$/;
/** The views a spec may open: a view id as the app's navigation knows it (`@OpenView`). */
export const viewId = z.string().regex(/^[a-z][a-z0-9-]{0,39}$/, "a view id");
const confirmText = z.string().min(1).max(UI_LIMITS.hint);
export const uiActionSchema = z.discriminatedUnion("do", [
    z.object({ do: z.literal("send"), text: z.string().min(1).max(UI_LIMITS.label * 8), confirm: confirmText.optional() }).strict(),
    z.object({ do: z.literal("open-view"), view: viewId, params: z.record(z.string().max(40), z.string().max(120)).optional() }).strict(),
    z.object({ do: z.literal("run"), name: z.string().regex(/^[a-z][a-z0-9_-]{0,39}$/), confirm: confirmText.optional() }).strict(),
    z.object({ do: z.literal("reset") }).strict(),
]);
//# sourceMappingURL=uiBase.js.map